import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import authService from '../services/authService';
import './LoginPage.css';

const initialForm = {
  new_password: '',
  re_new_password: '',
};

function getErrorMessages(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') {
    return { form: error?.message || 'Unable to reset your password. Please try again.' };
  }

  const fieldErrors = {};
  const formErrors = [];

  Object.entries(data).forEach(([field, messages]) => {
    const values = Array.isArray(messages) ? messages : [messages];
    if (field === 'non_field_errors' || field === 'detail') {
      formErrors.push(...values.filter(Boolean));
      return;
    }
    fieldErrors[field] = values.filter(Boolean).join(' ');
  });

  return {
    fieldErrors,
    form: formErrors.join(' ') || 'Please correct the highlighted fields.',
  };
}

export default function PasswordResetConfirmPage() {
  const { uid, token } = useParams();
  const [form, setForm] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setFieldErrors((current) => ({ ...current, [field]: '' }));
    setError('');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setFieldErrors({});

    try {
      await authService.confirmPasswordReset({ uid, token, ...form });
      setCompleted(true);
    } catch (resetError) {
      const messages = getErrorMessages(resetError);
      setFieldErrors(messages.fieldErrors || {});
      setError(messages.form || 'Please correct the highlighted fields.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!uid || !token) {
    return (
      <div className="login-page">
        <div className="card login-page__card">
          <div className="login-page__eyebrow">Invalid link</div>
          <h1 className="login-page__title">This reset link is incomplete</h1>
          <p className="login-page__description">Request a new password reset link and try again.</p>
          <Link to="/forgot-password" className="login-page__submit login-page__submit--link">Request new link</Link>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="login-page">
        <div className="card login-page__card">
          <div className="login-page__eyebrow">Password updated</div>
          <h1 className="login-page__title">You can sign in now</h1>
          <p className="login-page__description">Your password has been reset successfully.</p>
          <Link to="/login" className="login-page__submit login-page__submit--link">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="card login-page__card">
        <div className="login-page__eyebrow">Account recovery</div>
        <h1 className="login-page__title">Set a new password</h1>
        <p className="login-page__description">Choose a strong password for your ProfitFactor account.</p>

        <form onSubmit={onSubmit} className="login-page__form">
          <label className="login-page__label">
            New password
            <input
              className="login-page__input"
              type="password"
              value={form.new_password}
              onChange={updateField('new_password')}
              autoComplete="new-password"
              required
              aria-invalid={Boolean(fieldErrors.new_password)}
            />
            {fieldErrors.new_password ? <span className="login-page__field-error">{fieldErrors.new_password}</span> : null}
          </label>

          <label className="login-page__label">
            Confirm new password
            <input
              className="login-page__input"
              type="password"
              value={form.re_new_password}
              onChange={updateField('re_new_password')}
              autoComplete="new-password"
              required
              aria-invalid={Boolean(fieldErrors.re_new_password)}
            />
            {fieldErrors.re_new_password ? <span className="login-page__field-error">{fieldErrors.re_new_password}</span> : null}
          </label>

          {error ? <div className="login-page__error">{error}</div> : null}

          <button type="submit" disabled={submitting} className="login-page__submit">
            {submitting ? 'Updating…' : 'Reset password'}
          </button>
        </form>

        <p className="login-page__links">
          Need a new link? <Link to="/forgot-password">Request another reset</Link>
        </p>
      </div>
    </div>
  );
}
